import { useLayoutEffect } from 'react'
import { create } from 'zustand'
import type { ExportColumn } from '@/utils/exportUtils'
import { debugLog } from '@/utils/debugLog'

// Type for server-side API filter
export type ApiFilterOperator =
  | 'EQUALS'
  | 'CONTAINS'
  | 'GREATER_THAN'
  | 'LESS_THAN'
  | 'GREATER_THAN_OR_EQUAL_TO'
  | 'LESS_THAN_OR_EQUAL_TO'
  | 'NOT_EQUAL'
  | 'IS_EMPTY'
  | 'IS_NOT_EMPTY'

export type ApiFilter = {
  name: string
  value: string[]
  operator?: ApiFilterOperator
}

// One shared empty list, so a page that swaps its leaked filters for none does not see a new array.
const NO_FILTERS: ApiFilter[] = []

type TableState = {
  columnsStore: any[]
  setColumnsStore: (cols: any[]) => void
  rowsStore: any[]
  setRowsStore: (rows: any[]) => void
  filteredRowsStore: Record<string, { label: string; value: string }>
  setFilteredRowsStore: (
    rows: Record<string, { label: string; value: string }>
  ) => void
  exportRowsStore: any[]
  setExportRowsStore: (rows: any[]) => void
  exportColumnsStore: ExportColumn[]
  setExportColumnsStore: (cols: ExportColumn[]) => void
  clearFilteredRowsStore: () => void
  dateFilter: { from: string; to: string } | null
  setDateFilter: (filter: { from: string; to: string } | null) => void
  genericDateFilter: {
    from: string
    to: string
    fromValue: string
    toValue: string
  } | null
  setGenericDateFilter: (
    filter: {
      from: string
      to: string
      fromValue: string
      toValue: string
    } | null
  ) => void
  // Server-side API filters
  apiFilters: ApiFilter[]
  setApiFilters: (filters: ApiFilter[]) => void
  updateFieldFilter: (fieldName: string, values: string[], operator?: ApiFilterOperator) => void
  clearApiFilters: () => void
  // The page that owns apiFilters. A page that takes the store over starts empty.
  filterScope: string | null
  enterFilterScope: (scope: string) => void
  // The options a column search listed since its own filter first narrowed the
  // rows of a server page. Kept while that filter is active, so a second pick
  // is still offered.
  searchFilterOptions: Record<string, string[]>
  rememberSearchFilterOptions: (fieldName: string, options: string[]) => void
  // Table specific states
  tableStates: Record<string, { page: number; sortBy: string; sortOrder: 'ASC' | 'DESC' }>
  setTableState: (tableId: string, state: { page: number; sortBy: string; sortOrder: 'ASC' | 'DESC' }) => void
}

export const useTableStore = create<TableState>((set, get) => ({
  columnsStore: [],
  setColumnsStore: cols => set({ columnsStore: cols }),
  rowsStore: [],
  setRowsStore: rows => set({ rowsStore: rows }),
  filteredRowsStore: {},
  setFilteredRowsStore: rows => set({ filteredRowsStore: rows }),
  clearFilteredRowsStore: () => set({ filteredRowsStore: {} }),
  exportRowsStore: [],
  setExportRowsStore: rows => set({ exportRowsStore: rows }),
  exportColumnsStore: [],
  setExportColumnsStore: cols => set({ exportColumnsStore: cols }),
  dateFilter: null,
  setDateFilter: filter => set({ dateFilter: filter }),
  genericDateFilter: null,
  setGenericDateFilter: filter => set({ genericDateFilter: filter }),
  // Server-side API filters
  apiFilters: NO_FILTERS,
  setApiFilters: filters => set({ apiFilters: filters }),
  updateFieldFilter: (fieldName, values, operator) => {
    const currentFilters = get().apiFilters
    debugLog(`[Store] Updating filter for "${fieldName}":`, values, operator)
    // Remove existing filter for this field
    const otherFilters = currentFilters.filter(f => f.name !== fieldName)
    // Add new filter if values exist (or IS_EMPTY/IS_NOT_EMPTY which don't need values)
    if (values.length > 0 || operator === 'IS_EMPTY' || operator === 'IS_NOT_EMPTY') {
      const newFilters = [...otherFilters, { name: fieldName, value: values, ...(operator && { operator }) }]
      debugLog('[Store] New apiFilters state:', newFilters)
      set({ apiFilters: newFilters })
    } else {
      debugLog('[Store] Removing filter for:', fieldName)
      const searchFilterOptions = { ...get().searchFilterOptions }
      delete searchFilterOptions[fieldName]
      set({ apiFilters: otherFilters, searchFilterOptions })
    }
  },
  clearApiFilters: () => set({ apiFilters: [], searchFilterOptions: {} }),
  filterScope: null,
  enterFilterScope: scope => {
    if (get().filterScope === scope) return
    set({ filterScope: scope, apiFilters: NO_FILTERS, searchFilterOptions: {} })
  },
  searchFilterOptions: {},
  rememberSearchFilterOptions: (fieldName, options) =>
    set(state => ({
      searchFilterOptions: {
        ...state.searchFilterOptions,
        [fieldName]: options,
      },
    })),
  // Table specific states
  tableStates: {},
  setTableState: (tableId, state) =>
    set(prev => ({
      tableStates: {
        ...prev.tableStates,
        [tableId]: state,
      },
    })),
}))

/**
 * The column filters of one page. The page sees none until it owns the store,
 * and taking it over drops what the previous page left. Done in a layout
 * effect, not during render, so a page that is still unmounting never sees the
 * store change.
 */
export const useScopedApiFilters = (scope: string): ApiFilter[] => {
  useLayoutEffect(() => {
    useTableStore.getState().enterFilterScope(scope)
  }, [scope])
  return useTableStore(state =>
    state.filterScope === scope ? state.apiFilters : NO_FILTERS
  )
}
